import {
  LicenceDetailsState,
  type LicenceDetails,
  type ListingAgreementConfigurationResponse,
} from '@urbanx/agx.agencies.client';
import { AgxFormTextField } from '@urbanx/agx.forms';
import { AgxSpacer, AgxTypography, useToast } from '@urbanx/agx.ui-components2';
import { getAgencyLicenceStates } from 'components/settings-section/agencyLicenceStates';
import SettingsSection from 'components/settings-section/SettingsSection';
import SettingsPageHeader from 'components/settings-section/SettingsPageHeader';
import { FormPrompt } from 'components/FormPrompt';
import { getErrorMessage } from 'helpers/getErrorMessage';
import { useMemo, useState } from 'react';
import {
  maximumConfiguredFees,
  feeLimitMessage,
} from 'components/listing-agreement/lineItemSchemas';
import { LineItems } from 'components/listing-agreement/LineItems';
import { RebateListEditor } from 'components/listing-agreement/RebateListEditor';
import { ListingAgreementForm } from './listingAgreementFormDefinition';

export const ListingAgreementEditor = ({
  configuration,
  licences,
  onSave,
  onBack,
}: {
  configuration: ListingAgreementConfigurationResponse;
  onBack?: () => void;
  licences?: readonly Pick<LicenceDetails, 'state'>[] | null;
  onSave(
    configuration: ListingAgreementConfigurationResponse
  ): Promise<ListingAgreementConfigurationResponse>;
}) => {
  const { pushToast } = useToast();
  const [saveError, setSaveError] = useState<string>();
  const defaultValues = useMemo(
    () => ({
      eTag: configuration.eTag,
      privacyPolicyLink: configuration.settings.privacyPolicyLink ?? '',
      feesAndCharges: configuration.settings.feesAndCharges ?? [],
      rebates: configuration.settings.rebates ?? [],
    }),
    [configuration]
  );
  const form = ListingAgreementForm.useForm({
    defaultValues,
    disableWhileSubmitting: true,
    onSubmit: async ({ parsedValues }) => {
      setSaveError(undefined);
      try {
        const saved = await onSave({
          eTag: parsedValues.eTag,
          settings: {
            ...configuration.settings,
            privacyPolicyLink: parsedValues.privacyPolicyLink,
            feesAndCharges: parsedValues.feesAndCharges,
            rebates: parsedValues.rebates,
          },
        });
        pushToast({ type: 'success', message: 'Listing agreement saved successfully' });
        return {
          status: 'accepted',
          baseline: {
            eTag: saved.eTag,
            privacyPolicyLink: saved.settings.privacyPolicyLink ?? '',
            feesAndCharges: saved.settings.feesAndCharges ?? [],
            rebates: saved.settings.rebates ?? [],
          },
        };
      } catch (error) {
        const message = getErrorMessage(
          error,
          'Unable to save listing agreement settings. Please try again.',
          'Your draft is preserved.'
        );
        setSaveError(message);
        pushToast({ type: 'error', message });
        return { status: 'failed', error };
      }
    },
  });
  const states = getAgencyLicenceStates(licences);
  const showApplicability = states.length > 1;
  const rebateStates = [LicenceDetailsState.QLD, LicenceDetailsState.NSW].filter((state) =>
    states.includes(state)
  );
  const hasQld = states.includes(LicenceDetailsState.QLD);
  const hasRebates = hasQld || states.includes(LicenceDetailsState.NSW);
  return (
    <ListingAgreementForm.Root form={form}>
      <FormPrompt hasUnsavedChanges={form.isDirty} />
      <AgxSpacer gap="spacing-2xl" fill>
        <SettingsPageHeader
          id="listing-agreement"
          title="Listing Agreement"
          saving={form.phase === 'submitting'}
          disabled={!form.isDirty || form.phase === 'submitting'}
          onSave={() => void form.submit()}
          onBack={onBack}
        />
        {hasQld && (
          <SettingsSection
            id="privacy-policy"
            title="Privacy Policy"
            description="Link to the agency's privacy policy, referenced in the listing agreement."
            applicability={showApplicability ? 'QLD' : undefined}
            ariaLabel={showApplicability ? 'Privacy Policy (QLD)' : 'Privacy Policy'}
          >
            <ListingAgreementForm.Field
              name="privacyPolicyLink"
              Input={AgxFormTextField}
              type="text"
              label="Privacy Policy Link"
              hideOptionalText
              stretch
            />
          </SettingsSection>
        )}
        {hasQld && (
          <SettingsSection
            id="fees-and-charges"
            title="Description of Fees & Charges"
            description="Other fees and charges payable by the client, included in every listing agreement."
            applicability={showApplicability ? 'QLD' : undefined}
            ariaLabel={showApplicability ? 'Fees and Charges (QLD)' : 'Fees and Charges'}
          >
            <ListingAgreementForm.CustomField name="feesAndCharges">
              {(field) => (
                <LineItems
                  noun="fee or charge"
                  rowLabel="Fee or charge"
                  count={field.value.length}
                  maximumCount={maximumConfiguredFees}
                  limitMessage={feeLimitMessage}
                  disabled={field.disabled}
                  errorText={field.errorText}
                  onAdd={() =>
                    field.onChange([
                      ...field.value,
                      { description: '', amount: '', whenPayable: '' },
                    ])
                  }
                  onRemove={(index) =>
                    field.onChange(field.value.filter((_, row) => row !== index))
                  }
                  renderFields={(index) => [
                    <ListingAgreementForm.Field
                      name={`feesAndCharges[${index}].description`}
                      Input={AgxFormTextField}
                      type="text"
                      label="Description"
                      placeholder="e.g. Auctioneer fee"
                      key="description"
                      stretch
                    />,
                    <ListingAgreementForm.Field
                      name={`feesAndCharges[${index}].amount`}
                      Input={AgxFormTextField}
                      type="text"
                      label="Amount"
                      placeholder="e.g. $990 incl. GST"
                      key="amount"
                      stretch
                    />,
                    <ListingAgreementForm.Field
                      name={`feesAndCharges[${index}].whenPayable`}
                      Input={AgxFormTextField}
                      type="text"
                      label="When payable"
                      placeholder="e.g. On the day of auction"
                      key="whenPayable"
                      stretch
                    />,
                  ]}
                />
              )}
            </ListingAgreementForm.CustomField>
          </SettingsSection>
        )}
        {hasRebates && (
          <SettingsSection
            id="rebates"
            title="Rebates, Commissions and Benefits"
            description="Benefits the agency may receive from third parties, included in every listing agreement."
            applicability={showApplicability ? rebateStates.join(' & ') : undefined}
            ariaLabel={showApplicability ? `Rebates (${rebateStates.join(' and ')})` : 'Rebates'}
          >
            <ListingAgreementForm.CustomField name="rebates">
              {(field) => (
                <RebateListEditor
                  value={field.value}
                  disabled={field.disabled}
                  errorText={field.errorText}
                  onChange={field.onChange}
                  renderField={(index, { name, label, placeholder }) => (
                    <ListingAgreementForm.Field
                      key={name}
                      name={`rebates[${index}].${name}`}
                      Input={AgxFormTextField}
                      type="text"
                      label={label}
                      placeholder={placeholder}
                      stretch
                    />
                  )}
                />
              )}
            </ListingAgreementForm.CustomField>
          </SettingsSection>
        )}
        {!hasRebates && (
          <AgxTypography>
            Listing agreement settings are available for agencies licensed in QLD or NSW.
          </AgxTypography>
        )}
        {saveError && <AgxTypography type="form_error">{saveError}</AgxTypography>}
      </AgxSpacer>
    </ListingAgreementForm.Root>
  );
};
